import { useCallback, useEffect, useRef, useState, type DragEvent, type ReactNode } from 'react';
import {
  SETTINGS_ROLES,
  USER_COLORS,
  isServiceUserEmail,
  supervisorShown,
  type CustomRole,
  type SettingsRole,
  type UserTitle,
} from '@ehr/shared';
import { api } from '../lib/api.js';
import { askConfirm, showError } from '../lib/dialogs.js';
import { useSession } from '../auth/session.js';
import { Dd } from '../components/Dropdown.js';
import { notifyPhotosChanged, useTeamPhotos } from '../lib/userPhotos.js';
import { ianaZone, userStatus } from '../lib/userHours.js';
import {
  EMPTY_TEAM,
  HANDLE,
  IconClock,
  IconIdCard,
  IconPencil,
  IconPlus,
  IconSearch,
  IconShield,
  IconX,
  IconXSm,
  ROLE_LABEL,
  Shell,
  initials,
  msg,
  newId,
  roleBody,
  roleName,
  roleOptions,
  roleValue,
  uname,
  userColor,
  type Holiday,
  type Team,
  type TeamUser,
} from './usersShared.js';
import { HoursOverlay } from './UserHoursOverlay.js';
import { RoleRightsWindow, UserRightsWindow } from './UserRightsWindows.js';
import { UserGroupsWindow } from './UserGroupsWindow.js';
import { DashboardViewWindow } from './DashboardViewWindow.js';
import { UserProfileWindow } from './UserProfileWindow.js';
import { ProviderProfileFrame } from './ProviderProfileFrame.js';
import { titleDeletePrompt, titleUsers } from './titlesGuards.js';
import {
  NO_CUSTOM_ROLES_HINT,
  builtinLockNote,
  builtinLockTitle,
  rightsButtonTitle,
  roleDeleteBlock,
  saveButtonTitle,
} from './rolesGuards.js';
import './usersModal.css';

/**
 * Users & Profile (ehr#60) — the "Users & Profile" modal from EHR_08-14-V38_FINAL
 * (renderUserManagerModal) in place of the old Manage Users page: Add User (Profile →
 * Hours → Rights wizard), User Roles (+ Role Rights), User Titles, User Groups, Dashboard
 * View, and per user the role dropdown, Access Rights, Hours, Dashboard View, colour,
 * delete and group chips; Form Mapping and User Rights Mapping in the footer. A name or
 * pencil opens the Provider Profile (PROVIDER_PROFILE_09-30-V14, per Taras on ehr#60).
 * Access still comes from the five built-in roles; a custom role grants one of them.
 */

type View =
  | { kind: 'list' }
  | { kind: 'roles' }
  | { kind: 'titles' }
  | { kind: 'groups' }
  | { kind: 'dashboard'; userId?: string }
  | { kind: 'profile' }
  | { kind: 'provider'; id: string }
  | { kind: 'hours'; id: string; wizard?: boolean }
  | { kind: 'rights'; id: string; wizard?: 'role' | 'custom' };

const IconUsers = (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);
const IconGear = (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
  </svg>
);
const IconTrash = (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-2 14H7L5 6" />
  </svg>
);
export function UsersModal({
  onClose,
  openProfileId,
}: {
  onClose: () => void;
  /** ehr#232 - open straight onto this user's profile (the Calendar's Add provider link);
   *  closing the profile then closes the whole window. */
  openProfileId?: string;
}) {
  const { user: me } = useSession();
  // ehr#60 - Provider Profile photos, by user id; a photo replaces the initials swatch.
  const photos = useTeamPhotos();
  const [allUsers, setUsers] = useState<TeamUser[] | null>(null);
  const [team, setTeam] = useState<Team>(EMPTY_TEAM);
  const [holidays, setHolidays] = useState<Holiday[]>([]);
  const [tz, setTz] = useState<string | undefined>(undefined);
  const [err, setErr] = useState<string | null>(null);
  const [view, setView] = useState<View>(
    openProfileId ? { kind: 'provider', id: openProfileId } : { kind: 'list' },
  );
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [, tick] = useState(0);
  // Add User wizard: the rights mode picked on step 1 (role = step 3 is read-only).
  const wizardMode = useRef<'role' | 'custom'>('role');

  const load = useCallback(() => {
    Promise.all([api.get<TeamUser[]>('/settings/users'), api.get<Team>('/settings/team')])
      .then(([u, t]) => {
        setUsers(Array.isArray(u) ? u : []);
        setTeam({ ...EMPTY_TEAM, ...(t ?? {}) });
        setErr(null);
      })
      .catch((e: unknown) =>
        setErr(
          (e as { status?: number }).status === 403
            ? 'Only admins and managers can manage users.'
            : `Could not load the team. ${msg(e)}`,
        ),
      );
  }, []);
  useEffect(() => {
    load();
    // The status pill needs the practice time zone + holidays; both are optional.
    api
      .get<{ timezone?: string }>('/practice/profile')
      .then((p) => setTz(ianaZone(p?.timezone)))
      .catch(() => undefined);
    api
      .get<Holiday[]>('/practice/holidays')
      .then((h) => setHolidays(Array.isArray(h) ? h : []))
      .catch(() => undefined);
    const t = window.setInterval(() => tick((n) => n + 1), 60_000);
    return () => window.clearInterval(t);
  }, [load]);

  // ESC closes the top layer first (popups with their own Escape are skipped).
  const viewRef = useRef(view);
  viewRef.current = view;
  useEffect(() => {
    const esc = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // Capture phase, so this runs before React: an open dropdown or a rename popup
      // handles its own Escape.
      if (e.target instanceof Element && e.target.closest('.dd-open, .dtf-open, .um-rename-ov'))
        return;
      if (viewRef.current.kind !== 'list') setView({ kind: 'list' });
      else onClose();
    };
    document.addEventListener('keydown', esc, true);
    return () => document.removeEventListener('keydown', esc, true);
  }, [onClose]);

  // Deleted users are deactivated; system accounts (the portal's portal-svc+…) are hidden:
  // changing or deleting one would break the client portal.
  const users = (allUsers ?? []).filter((u) => u.active && !isServiceUserEmail(u.email));
  const back = () => (openProfileId ? onClose() : setView({ kind: 'list' }));
  const byId = (id: string) => allUsers?.find((u) => u.id === id);
  const patch = (u: TeamUser, body: Record<string, unknown>) => {
    setBusy(u.id);
    api
      .patch(`/settings/users/${u.id}`, body)
      .then(load)
      .catch((e: unknown) => {
        showError(msg(e));
        load(); // snap back to the unchanged value
      })
      .finally(() => setBusy(null));
  };
  const changeRole = (u: TeamUser, v: string) => {
    const body = roleBody(v, team);
    if (body) patch(u, body);
  };
  // ehr#320 - Supervisor: who signs off this person's notes and forms ('-' = no supervisor).
  const supervisorOf = (u: TeamUser) =>
    supervisorShown(u.supervision ?? { required: false, supervisorId: null });
  const supervisorOptions = (u: TeamUser) => {
    const cur = supervisorOf(u);
    const opts = users
      .filter((x) => x.id !== u.id && !x.associate)
      .map((x) => ({ value: x.id, label: uname(x) }));
    if (cur && !opts.some((o) => o.value === cur)) {
      const gone = byId(cur);
      opts.push({ value: cur, label: gone ? `${uname(gone)} (inactive)` : 'Unknown user' });
    }
    return cur ? [{ value: '-', label: 'No supervisor' }, ...opts] : opts;
  };
  const changeSupervisor = (u: TeamUser, v: string) => {
    const supervisorId = v === '-' ? null : v;
    if (supervisorId === (supervisorOf(u) || null)) return;
    setBusy(u.id);
    api
      .put(`/settings/users/${u.id}/supervisor`, { supervisorId })
      .then(load)
      .catch((e: unknown) => {
        showError(msg(e));
        load();
      })
      .finally(() => setBusy(null));
  };
  const cycleColor = (u: TeamUser) => {
    const cur = USER_COLORS.indexOf((u.color ?? '') as (typeof USER_COLORS)[number]);
    patch(u, { color: USER_COLORS[(cur + 1) % USER_COLORS.length] });
  };
  const remove = async (u: TeamUser) => {
    if (
      !(await askConfirm(
        `Delete ${uname(u)}?\n\nThey can no longer sign in, and the contacts they own move to you.`,
      ))
    )
      return;
    setBusy(u.id);
    api
      .del(`/settings/users/${u.id}`)
      .then(load)
      .catch((e: unknown) => showError(msg(e)))
      .finally(() => setBusy(null));
  };
  const groupsOf = (u: TeamUser) => team.groups.filter((g) => g.memberIds.includes(u.id));

  const ql = q.trim().toLowerCase();
  const shown = users.filter(
    (u) =>
      !ql ||
      [uname(u), u.email, u.title ?? '', roleName(u, team), u.contactInfo?.phone ?? ''].some((s) =>
        s.toLowerCase().includes(ql),
      ),
  );

  const list = (
    <div
      className="modal-lg modal-wide um-modal"
      role="dialog"
      aria-modal="true"
      aria-label="Users & Profile"
    >
      <div className="modal-head">
        <div>
          <h2>Users &amp; Profile</h2>
          <p>
            Manage your team — roles, titles, access rights, and hours. Click a teammate’s name to
            edit their profile.
          </p>
        </div>
        <button className="modal-close" onClick={onClose} aria-label="Close">
          {IconX}
        </button>
      </div>
      <div className="modal-body">
        <div className="um-toolbar">
          <button className="btn primary" onClick={() => setView({ kind: 'profile' })}>
            {IconPlus} Add User
          </button>
          <button
            className="btn"
            title="Add, rename, reorder, and delete roles"
            onClick={() => setView({ kind: 'roles' })}
          >
            {IconShield} User Roles
          </button>
          <button
            className="btn"
            title="Manage the list of job titles"
            onClick={() => setView({ kind: 'titles' })}
          >
            {IconIdCard} User Titles
          </button>
          <button
            className="btn"
            title="Manage user groups"
            onClick={() => setView({ kind: 'groups' })}
          >
            {IconUsers} User Groups
          </button>
          <button
            className="btn"
            title="Choose which lists appear on the dashboard, org-wide"
            onClick={() => setView({ kind: 'dashboard' })}
          >
            {IconGear} Dashboard View
          </button>
        </div>
        {err && <div className="config-err">{err}</div>}
        <div className="user-mgr-section-label">
          Team <span className="um-count">· {users.length}</span>
        </div>
        <div className="user-search-wrap">
          {IconSearch}
          <input
            type="text"
            className="user-search-input"
            placeholder="Search users by name, title, email, or role…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          {q && (
            <button className="user-search-clear" title="Clear" onClick={() => setQ('')}>
              ×
            </button>
          )}
        </div>
        <div className="user-mgr-list">
          {allUsers === null && !err ? (
            <div className="user-mgr-empty">Loading…</div>
          ) : shown.length === 0 ? (
            <div className="user-mgr-empty">{ql ? `No users match "${q}".` : 'No users yet.'}</div>
          ) : (
            shown.map((u) => {
              const isMe = u.id === me?.id;
              const st = userStatus(u.hours, holidays, tz);
              const gs = groupsOf(u);
              return (
                <div key={u.id} className={`user-mgr-row${isMe ? ' is-me' : ''}`}>
                  <div className="user-mgr-row-top">
                    {photos[u.id] ? (
                      <span
                        className="color-swatch has-photo"
                        title="Photo from the Provider Profile"
                      >
                        <img src={photos[u.id]} alt={`Photo of ${uname(u)}`} />
                      </span>
                    ) : (
                      <button
                        className="color-swatch"
                        style={{ background: userColor(u) }}
                        title="Click to change color"
                        aria-label={`Change ${uname(u)}'s color`}
                        disabled={busy === u.id}
                        onClick={() => cycleColor(u)}
                      >
                        {initials(u)}
                      </button>
                    )}
                    <button
                      className="user-name-link"
                      title="Edit profile & contact info"
                      onClick={() => setView({ kind: 'provider', id: u.id })}
                    >
                      {uname(u)}
                      {u.title && <span className="user-mgr-title-inline"> — {u.title}</span>}
                    </button>
                    {isMe && <span className="user-mgr-me-tag">You</span>}
                    <span className={`user-status-pill us-${st.tone}`} title={st.full}>
                      <span className="us-dot" />
                      {st.short}
                    </span>
                    {/* ehr#232 - providers vs support staff; change it on the profile. */}
                    <span
                      className="user-status-pill us-grey user-sees-pill"
                      title={
                        u.seesClients
                          ? 'Sees clients: listed wherever a provider is picked. Change it on their profile.'
                          : 'Support staff: not listed as a provider. Change it on their profile.'
                      }
                    >
                      {u.seesClients ? 'Sees clients' : 'Support staff'}
                    </span>
                  </div>
                  <div className="user-mgr-row-controls">
                    <Dd
                      floating
                      searchable={team.roles.length > 4}
                      value={roleValue(u, team)}
                      disabled={busy === u.id}
                      onChange={(v) => changeRole(u, v)}
                      ariaLabel={`Role for ${uname(u)}`}
                      options={roleOptions(team)}
                    />
                    {/* ehr#320 - who signs off their notes and forms (Provider Profile →
                        Forms and Supervision). */}
                    <Dd
                      floating
                      className="user-sup-dd"
                      searchable={users.length > 8}
                      value={supervisorOf(u)}
                      placeholder="Select Supervisor"
                      disabled={busy === u.id}
                      onChange={(v) => changeSupervisor(u, v)}
                      ariaLabel={`Supervisor for ${uname(u)}`}
                      options={supervisorOptions(u)}
                    />
                    <button
                      className="user-access-btn"
                      title="Set this user's feature access rights"
                      onClick={() => setView({ kind: 'rights', id: u.id })}
                    >
                      {IconShield}Access Rights
                    </button>
                    <button
                      className={`user-hours-btn${u.hours ? '' : ' unset'}`}
                      title="Set operational hours"
                      onClick={() => setView({ kind: 'hours', id: u.id })}
                    >
                      {IconClock}Hours
                    </button>
                    <button
                      className={`user-dv-btn ${u.dashboardView ? 'configured' : 'unset'}`}
                      title={
                        u.dashboardView
                          ? 'Dashboard View is configured for this user'
                          : 'Dashboard View not yet configured for this user — using the org default'
                      }
                      onClick={() => setView({ kind: 'dashboard', userId: u.id })}
                    >
                      {IconGear}Dashboard View
                    </button>
                    <button
                      className="user-mgr-expand"
                      title="Edit profile"
                      aria-label={`Edit ${uname(u)}`}
                      onClick={() => setView({ kind: 'provider', id: u.id })}
                    >
                      {IconPencil}
                    </button>
                    <button
                      className="row-delete"
                      disabled={isMe || users.length === 1 || busy === u.id}
                      title={
                        isMe
                          ? 'You can’t delete your own account'
                          : users.length === 1
                            ? 'Cannot delete the last user'
                            : 'Delete user'
                      }
                      aria-label={`Delete ${uname(u)}`}
                      onClick={() => remove(u)}
                    >
                      {IconTrash}
                    </button>
                  </div>
                  {gs.length > 0 && (
                    <div className="user-mgr-groups">
                      {gs.map((g) => (
                        <span className="user-mgr-group-chip" key={g.id}>
                          {g.name}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
        <p className="um-help">
          Click a teammate’s name (or the pencil) to edit their profile and contact info. A
          supervisor co-signs every note and form that teammate signs (the same setting as Provider
          Profile → Forms and Supervision). Deleting a user moves the contacts they own to you and
          stops them signing in.
        </p>
      </div>
      <div className="modal-foot">
        <div className="um-foot-left">
          {q && (
            <button className="btn" title="Back to all users" onClick={() => setQ('')}>
              {IconUsers} All Users
            </button>
          )}
          <button className="btn primary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );

  const roleKeyOf = (u: TeamUser) => roleValue(u, team);
  const roleRightsOf = (u: TeamUser) => team.roleRights[roleKeyOf(u)] ?? {};

  let body: ReactNode = list;
  let overlay: ReactNode = null;
  if (view.kind === 'roles') {
    body = (
      <RolesView team={team} users={users} onBack={back} onSaved={load} onRightsSaved={load} />
    );
  } else if (view.kind === 'titles') {
    body = <TitlesView team={team} users={users} onBack={back} onSaved={load} />;
  } else if (view.kind === 'groups') {
    body = (
      <UserGroupsWindow
        groups={team.groups}
        users={users}
        roleLabel={(u) => roleName(u, team)}
        onBack={back}
        onSaved={load}
      />
    );
  } else if (view.kind === 'dashboard') {
    const target = view.userId ? byId(view.userId) : undefined;
    body = (
      <DashboardViewWindow
        team={team}
        users={users}
        initial={target?.dashboardView ?? team.dashboardView}
        preselectUserId={view.userId}
        onBack={back}
        onSaved={load}
      />
    );
  } else if (view.kind === 'profile') {
    overlay = (
      <UserProfileWindow
        team={team}
        step="Step 1 of 3 — Profile"
        onClose={back}
        onNext={(r) => {
          load();
          setView({ kind: 'hours', id: r.id, wizard: true });
          wizardMode.current = r.rightsMode;
        }}
      />
    );
  } else if (view.kind === 'provider') {
    overlay = (
      <ProviderProfileFrame
        userId={view.id}
        onClose={back}
        // ehr#524: the profile's Edit signature opens the editor over the profile itself
        onSaved={() => {
          load();
          notifyPhotosChanged(); // ehr#60 - a new photo shows in every avatar
        }}
      />
    );
  } else if (view.kind === 'hours' && byId(view.id)) {
    const u = byId(view.id)!;
    const toRights = () => setView({ kind: 'rights', id: u.id, wizard: wizardMode.current });
    overlay = (
      <HoursOverlay
        user={u}
        roleLabel={roleName(u, team)}
        status={userStatus(u.hours, holidays, tz)}
        holidays={holidays}
        wizard={view.wizard ? { onBack: back, onSkip: toRights } : undefined}
        onBack={back}
        onSaved={() => {
          load();
          if (view.wizard) toRights();
          else back();
        }}
      />
    );
  } else if (view.kind === 'rights' && byId(view.id)) {
    const u = byId(view.id)!;
    const wiz = view.wizard;
    overlay = (
      <UserRightsWindow
        key={u.id}
        userId={u.id}
        userName={uname(u)}
        roleName={roleName(u, team)}
        baseRole={u.role}
        roleRights={roleRightsOf(u)}
        initial={u.rights}
        wizard={!!wiz}
        onClose={back}
        onSaved={load}
        footer={
          wiz ? (
            <>
              <span className="up-step">Step 3 of 3 — Rights</span>
              <button
                className="btn"
                onClick={() => setView({ kind: 'hours', id: u.id, wizard: true })}
              >
                Back
              </button>
              <button className="btn primary" onClick={back}>
                Finish
              </button>
            </>
          ) : undefined
        }
      />
    );
  }

  return (
    <>
      <div
        className="modal-backdrop"
        onMouseDown={(e) => e.target === e.currentTarget && onClose()}
      >
        {body}
      </div>
      {overlay}
    </>
  );
}

// ── Shared list helpers (roles + titles) ─────────────────────────────────────
function useDragList<T extends { id: string }>(items: T[], setItems: (v: T[]) => void) {
  const drag = useRef<string | null>(null);
  return (id: string) => ({
    draggable: true,
    onDragStart: (e: DragEvent<HTMLDivElement>) => {
      drag.current = id;
      e.currentTarget.classList.add('ctm-dragging');
    },
    onDragEnd: (e: DragEvent<HTMLDivElement>) => e.currentTarget.classList.remove('ctm-dragging'),
    onDragOver: (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.currentTarget.classList.add('ctm-drop-before');
    },
    onDragLeave: (e: DragEvent<HTMLDivElement>) =>
      e.currentTarget.classList.remove('ctm-drop-before'),
    onDrop: (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.currentTarget.classList.remove('ctm-drop-before');
      const from = items.findIndex((x) => x.id === drag.current);
      const to = items.findIndex((x) => x.id === id);
      drag.current = null;
      if (from < 0 || to < 0 || from === to) return;
      const next = items.slice();
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved!);
      setItems(next);
    },
  });
}

/** The mockup's rename popup (#role-rename-overlay / #label-rename-overlay). */
function RenamePopup({
  heading,
  value,
  placeholder,
  validate,
  onSave,
  onCancel,
}: {
  heading: string;
  value: string;
  placeholder: string;
  validate: (v: string) => string | null;
  onSave: (v: string) => void;
  onCancel: () => void;
}) {
  const [v, setV] = useState(value);
  const [err, setErr] = useState<string | null>(null);
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    ref.current?.focus();
    ref.current?.select();
  }, []);
  const save = () => {
    const name = v.trim();
    const e = validate(name);
    if (e) return setErr(e);
    onSave(name);
  };
  return (
    <div className="um-rename-ov" onMouseDown={(e) => e.target === e.currentTarget && onCancel()}>
      <div className="um-rename-box" role="dialog" aria-modal="true" aria-label={heading}>
        <div className="um-rename-head">
          <div className="um-rename-title">{heading}</div>
          <button className="uh-x" title="Cancel" onClick={onCancel}>
            {IconX}
          </button>
        </div>
        <div className="um-rename-body">
          <input
            ref={ref}
            type="text"
            value={v}
            maxLength={80}
            placeholder={placeholder}
            onChange={(e) => {
              setV(e.target.value);
              setErr(null);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                save();
              } else if (e.key === 'Escape') {
                e.preventDefault();
                onCancel();
              }
            }}
          />
          {err && <div className="um-rename-err">{err}</div>}
        </div>
        <div className="um-rename-foot">
          <button className="btn" onClick={onCancel}>
            Cancel
          </button>
          <button className="btn primary" onClick={save}>
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

const UserCount = ({ n }: { n: number }) => (
  <span className="ctm-tag um-count-tag">
    {n} user{n === 1 ? '' : 's'}
  </span>
);

const IconSliders = (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <line x1="4" y1="21" x2="4" y2="14" />
    <line x1="4" y1="10" x2="4" y2="3" />
    <line x1="12" y1="21" x2="12" y2="12" />
    <line x1="12" y1="8" x2="12" y2="3" />
    <line x1="20" y1="21" x2="20" y2="16" />
    <line x1="20" y1="12" x2="20" y2="3" />
    <line x1="1" y1="14" x2="7" y2="14" />
    <line x1="9" y1="8" x2="15" y2="8" />
    <line x1="17" y1="16" x2="23" y2="16" />
  </svg>
);

function RolesView({
  team,
  users,
  onBack,
  onSaved,
  onRightsSaved,
}: {
  team: Team;
  users: TeamUser[];
  onBack: () => void;
  onSaved: () => void;
  /** Role rights save on their own (not with the role list); refresh the team after. */
  onRightsSaved: () => void;
}) {
  const [roles, setRoles] = useState<CustomRole[]>(team.roles);
  const [renaming, setRenaming] = useState<string | null>(null);
  const [rightsFor, setRightsFor] = useState<{
    key: string;
    name: string;
    base: SettingsRole;
  } | null>(null);
  const [roleRights, setRoleRights] = useState(team.roleRights);
  const [q, setQ] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  /** ehr#709 - why a locked control did nothing (built-in role, role still in use). */
  const [note, setNote] = useState<string | null>(null);
  const dragProps = useDragList(roles, setRoles);
  const dirty = JSON.stringify(roles) !== JSON.stringify(team.roles);
  useEffect(() => setRoleRights(team.roleRights), [team.roleRights]);

  const count = (pred: (u: TeamUser) => boolean) => users.filter(pred).length;
  /** V38 roleRightsConfigured: built-ins and roles with any right read "Edit Role Rights". */
  const rightsBtn = (key: string, name: string, base: SettingsRole, saved: boolean) => {
    const set = key.startsWith('sys:') || Object.values(roleRights[key] ?? {}).some(Boolean);
    return (
      <button
        className={`role-rights-btn ${set ? 'set' : 'unset'}`}
        disabled={!saved}
        title={rightsButtonTitle(key, saved, set)}
        onClick={() => setRightsFor({ key, name, base })}
      >
        {IconSliders}
        {set ? 'Edit Role Rights' : 'Add Role Rights'}
      </button>
    );
  };
  const nameTaken = (name: string, id?: string) =>
    SETTINGS_ROLES.some((r) => ROLE_LABEL[r].toLowerCase() === name.toLowerCase()) ||
    roles.some((r) => r.id !== id && r.name.toLowerCase() === name.toLowerCase());

  const add = () => {
    let name = 'New role';
    for (let i = 2; nameTaken(name); i++) name = `New role ${i}`;
    const id = newId('role');
    setRoles([...roles, { id, name, baseRole: 'clinician' }]);
    setRenaming(id);
  };
  const remove = async (r: CustomRole) => {
    // ehr#709 - a role still held by active users can't go (the server refuses too).
    const blocked = roleDeleteBlock(r, users);
    if (blocked) {
      setNote(blocked);
      return;
    }
    setNote(null);
    if (!(await askConfirm(`Delete the role “${r.name}”?`))) return;
    setRoles((rs) => rs.filter((x) => x.id !== r.id));
  };
  const save = () => {
    setSaving(true);
    api
      .put('/settings/team/roles', { roles })
      .then(() => {
        onSaved();
        onBack();
      })
      .catch((e: unknown) => setErr(msg(e)))
      .finally(() => setSaving(false));
  };

  const ql = q.trim().toLowerCase();
  const sys = SETTINGS_ROLES.filter((r) => !ql || ROLE_LABEL[r].toLowerCase().includes(ql));
  const custom = roles.filter((r) => !ql || r.name.toLowerCase().includes(ql));
  const baseOpts = SETTINGS_ROLES.map((r) => ({ value: r, label: `${ROLE_LABEL[r]} access` }));
  const renameRole = roles.find((r) => r.id === renaming);

  return (
    <>
      <Shell
        title="User Roles"
        sub="Roles your users can hold. Add your own, rename, and drag to reorder. Assign a role to each user on their row in Users & Profile. Built-in roles are fixed; each of your roles gives the access of one built-in role."
        onClose={onBack}
        foot={
          <>
            <button className="btn" onClick={onBack}>
              {dirty ? 'Cancel' : 'Close'}
            </button>
            <button
              className="btn primary"
              disabled={!dirty || saving}
              title={saveButtonTitle(dirty, saving)}
              onClick={save}
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </>
        }
      >
        <div className="ctm-toolbar">
          <input
            type="text"
            className="ctm-search-input"
            placeholder="Search roles…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <button
            className="btn primary"
            title="Add a role you can rename, set rights for, and delete"
            onClick={() => {
              setNote(null);
              add();
            }}
          >
            + Add Role
          </button>
        </div>
        {err && <div className="config-err">{err}</div>}
        {note ? (
          <div className="um-role-note" role="status">
            <span>{note}</span>
            <button className="ctm-icon" title="Dismiss" onClick={() => setNote(null)}>
              {IconXSm}
            </button>
          </div>
        ) : (
          roles.length === 0 && <div className="um-role-hint">{NO_CUSTOM_ROLES_HINT}</div>
        )}
        {sys.length + custom.length === 0 ? (
          <div className="ctm-noresults">No matches for your search.</div>
        ) : (
          <div className="ctm-list">
            {sys.map((r) => (
              <div className="ctm-row" key={r}>
                <span className="ctm-handle um-locked" aria-hidden>
                  {HANDLE}
                </span>
                <span className="ctm-num" />
                <div className="ctm-name">
                  <span className="ctm-name-text">{ROLE_LABEL[r]}</span>
                  <span className="ctm-tag">built-in</span>
                  <UserCount
                    n={count(
                      (u) => u.role === r && !team.roles.some((c) => c.id === u.customRoleId),
                    )}
                  />
                </div>
                {rightsBtn(`sys:${r}`, ROLE_LABEL[r], r, true)}
                <div className="ctm-acts">
                  {/* ehr#709 - locked, but a click says why instead of a silent grey icon. */}
                  <button
                    className="ctm-icon locked"
                    aria-disabled="true"
                    title={builtinLockTitle('rename')}
                    onClick={() => setNote(builtinLockNote(ROLE_LABEL[r], 'rename'))}
                  >
                    {IconPencil}
                  </button>
                  <button
                    className="ctm-icon danger locked"
                    aria-disabled="true"
                    title={builtinLockTitle('delete')}
                    onClick={() => setNote(builtinLockNote(ROLE_LABEL[r], 'delete'))}
                  >
                    {IconXSm}
                  </button>
                </div>
              </div>
            ))}
            {custom.map((r) => (
              <div className="ctm-row" key={r.id} {...dragProps(r.id)}>
                <span className="ctm-handle" aria-hidden title="Drag to reorder">
                  {HANDLE}
                </span>
                <span className="ctm-num" />
                <div className="ctm-name">
                  <span className="ctm-name-text">{r.name}</span>
                  <UserCount n={count((u) => u.customRoleId === r.id)} />
                </div>
                <span className="um-base" title="What this role can see and do">
                  <Dd
                    floating
                    searchable={false}
                    value={r.baseRole}
                    ariaLabel={`Access for ${r.name}`}
                    onChange={(v) =>
                      setRoles(
                        roles.map((x) =>
                          x.id === r.id ? { ...x, baseRole: v as SettingsRole } : x,
                        ),
                      )
                    }
                    options={baseOpts}
                  />
                </span>
                {rightsBtn(
                  `cr:${r.id}`,
                  r.name,
                  r.baseRole,
                  team.roles.some((x) => x.id === r.id),
                )}
                <div className="ctm-acts">
                  <button className="ctm-icon" title="Rename" onClick={() => setRenaming(r.id)}>
                    {IconPencil}
                  </button>
                  <button className="ctm-icon danger" title="Delete" onClick={() => remove(r)}>
                    {IconXSm}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Shell>
      {renameRole && (
        <RenamePopup
          heading="Role name"
          value={renameRole.name}
          placeholder="e.g. Intake Coordinator"
          validate={(v) =>
            !v
              ? 'Role name is required'
              : nameTaken(v, renameRole.id)
                ? 'A role with that name already exists'
                : null
          }
          onSave={(name) => {
            setRoles(roles.map((r) => (r.id === renameRole.id ? { ...r, name } : r)));
            setRenaming(null);
          }}
          onCancel={() => setRenaming(null)}
        />
      )}
      {rightsFor && (
        <RoleRightsWindow
          roleKey={rightsFor.key}
          roleName={rightsFor.name}
          rights={roleRights[rightsFor.key] ?? {}}
          onClose={() => setRightsFor(null)}
          onSaved={(r) => {
            setRoleRights({ ...roleRights, [rightsFor.key]: r });
            onRightsSaved();
          }}
        />
      )}
    </>
  );
}

function TitlesView({
  team,
  users,
  onBack,
  onSaved,
}: {
  team: Team;
  /** ehr#710 - who holds each title (saved as text on the user). */
  users: TeamUser[];
  onBack: () => void;
  onSaved: () => void;
}) {
  const [titles, setTitles] = useState<UserTitle[]>(team.titles);
  const [renaming, setRenaming] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const dragProps = useDragList(titles, setTitles);
  const dirty = JSON.stringify(titles) !== JSON.stringify(team.titles);
  const taken = (label: string, id?: string) =>
    titles.some((t) => t.id !== id && t.label.toLowerCase() === label.toLowerCase());

  const add = () => {
    let label = 'New title';
    for (let i = 2; taken(label); i++) label = `New title ${i}`;
    const id = newId('utl');
    setTitles([...titles, { id, label }]);
    setRenaming(id);
  };
  const save = () => {
    setSaving(true);
    api
      .put('/settings/team/titles', { titles })
      .then(() => {
        onSaved();
        onBack();
      })
      .catch((e: unknown) => setErr(msg(e)))
      .finally(() => setSaving(false));
  };
  const ql = q.trim().toLowerCase();
  const shown = titles.filter((t) => !ql || t.label.toLowerCase().includes(ql));
  const renameTitle = titles.find((t) => t.id === renaming);

  return (
    <>
      <Shell
        title="User Titles"
        sub="Job titles your users can hold. Add your own, rename, and drag to reorder. Deleting or renaming a title here never changes a user’s profile."
        onClose={onBack}
        foot={
          <>
            <button className="btn" onClick={onBack}>
              {dirty ? 'Cancel' : 'Close'}
            </button>
            <button className="btn primary" disabled={!dirty || saving} onClick={save}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          </>
        }
      >
        <div className="ctm-toolbar">
          <input
            type="text"
            className="ctm-search-input"
            placeholder="Search titles…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <button className="btn primary" onClick={add}>
            + Add Title
          </button>
        </div>
        {err && <div className="config-err">{err}</div>}
        {titles.length === 0 ? (
          <div className="ctm-empty">No titles yet. Click “+ Add Title” above.</div>
        ) : shown.length === 0 ? (
          <div className="ctm-noresults">No matches for your search.</div>
        ) : (
          <div className="ctm-list">
            {shown.map((t) => (
              <div className="ctm-row" key={t.id} {...dragProps(t.id)}>
                <span className="ctm-handle" aria-hidden title="Drag to reorder">
                  {HANDLE}
                </span>
                <span className="ctm-num" />
                <div className="ctm-name">
                  <span className="ctm-name-text">{t.label}</span>
                  <UserCount n={titleUsers(t.label, users)} />
                </div>
                <div className="ctm-acts">
                  <button className="ctm-icon" title="Rename" onClick={() => setRenaming(t.id)}>
                    {IconPencil}
                  </button>
                  <button
                    className="ctm-icon danger"
                    title="Delete"
                    onClick={() =>
                      void askConfirm(titleDeletePrompt(t.label, titleUsers(t.label, users))).then(
                        (ok) => ok && setTitles((ts) => ts.filter((x) => x.id !== t.id)),
                      )
                    }
                  >
                    {IconXSm}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Shell>
      {renameTitle && (
        <RenamePopup
          heading="Title name"
          value={renameTitle.label}
          placeholder="e.g. Clinical Director"
          validate={(v) =>
            !v
              ? 'Title name is required'
              : taken(v, renameTitle.id)
                ? 'A title with that name already exists'
                : null
          }
          onSave={(label) => {
            setTitles(titles.map((t) => (t.id === renameTitle.id ? { ...t, label } : t)));
            setRenaming(null);
          }}
          onCancel={() => setRenaming(null)}
        />
      )}
    </>
  );
}
